// 라이라(궁수) 게임 이벤트 → 렌더 연결(c022 D4a 레인 C). 렌더 onEvent가 view.heroId === 'lyra'일 때 이벤트마다 부른다.
//   Ev.Arrow    → 화살 렌더(같은 속도·사거리, 늦게 온 만큼 앞에서 시작) + 기본 공격이면 발사 큐(lyra_shot / lyra_shot_heavy) + 시위 놓기
//   Ev.ArrowEnd → 그 슬롯 화살을 끝점에서 멈춤(적중·벽이면 즉시), 벽 착탄은 작은 바람 고리
//   Ev.Hit      → 관통 타격(HitFlag.Pierce)이면 맞은 자리 작은 바람 고리
//   Ev.SkillCue → vfx 채널: 라이라 표 이름(cueNameOf(…, 'lyra', 갈래 = flags & 2))으로 LyraFx.playCue. 세계 위치 큐(flags & 1)는 그 지점이 중심(ahead 0)
//   각성        → 모으기 lyra_awaken_charge(영웅 위치), 난무 한 파 lyra_awaken_rain(영웅 위치)
// 이벤트 처리 중 새 객체를 만들지 않는다(숫자만 넘긴다).
import { Ev, HitFlag, type GameEvent } from '../shared/events';
import { AwakenPhase } from '../shared/types';
import { ArrowKind } from '../vfx/arrows';
import type { LyraFx } from '../vfx/lyraFx';
import { cueNameOf } from '../vfx/modules/cueMap';
import { BowDraw } from './lyraPose';

/** 게임 화살 종류(skills/types ArrowKind 0..7) → 화살 렌더 종류(vfx/arrows ArrowKind 0..5). 6 올려쏘기 = 강사 모양, 7 회오리 = 관통 모양 */
export const GAME_TO_FX_ARROW: readonly number[] = [
  ArrowKind.Basic, ArrowKind.Heavy, ArrowKind.Pierce, ArrowKind.Explode, ArrowKind.Air, ArrowKind.Homing, ArrowKind.Heavy, ArrowKind.Pierce,
];
/** 게임 발사 높이 기본값(game/arrows.ts ARROW_Y): 발사 큐는 영웅 바닥 높이 기준이라 이만큼 뺀다 */
export const ARROW_LAUNCH_Y = 1.2;

export interface LyraLinkStats { arrows: number; ends: number; cues: number; unknownCues: number; pierce: number }

export class LyraLink {
  readonly draw = new BowDraw();
  readonly stats: LyraLinkStats = { arrows: 0, ends: 0, cues: 0, unknownCues: 0, pierce: 0 };

  constructor(readonly fx: LyraFx) {}

  /**
   * 이벤트 하나. lag = 이벤트가 늦게 도착한 시간(초, 영웅 시계). heroX/Y/Z·facing = 지금 영웅 표시 위치(각성 큐 중심).
   * 반환: true면 이 이벤트의 카엘 연출 경로(VfxModules 스킬 큐)를 건너뛴다
   */
  onEvent(e: GameEvent, lag: number, heroX: number, heroY: number, heroZ: number, facing: number): boolean {
    switch (e.type) {
      case Ev.Arrow: {
        const k = e.d | 0;
        const kind = GAME_TO_FX_ARROW[k] ?? ArrowKind.Basic;
        this.fx.arrows.fire(e.x, e.y, e.z, Math.sin(e.a), Math.cos(e.a), e.b, e.c, kind, e.flags >> 8, lag);
        this.stats.arrows++;
        this.draw.release();
        // 기본 공격 발사 큐(스킬 화살의 발사 연출은 스킬 큐가 낸다)
        if ((e.flags & 2) === 0) this.fx.playCue(k === 1 ? 'lyra_shot_heavy' : 'lyra_shot', e.x, Math.max(0, e.y - ARROW_LAUNCH_Y), e.z, e.a, lag);
        return true;
      }
      case Ev.ArrowEnd: {
        this.fx.arrows.end(e.a | 0, e.x, e.z);
        this.stats.ends++;
        // 벽 착탄: 벽면 작은 바람 고리(적중은 Ev.Hit 쪽 연출, 사거리 끝은 꼬리만 사라진다)
        if ((e.b | 0) === 2) this.fx.pierceHit(e.x, ARROW_LAUNCH_Y, e.z, facing);
        return true;
      }
      case Ev.Hit:
        if (e.b !== 2 && (e.flags & HitFlag.Pierce) !== 0) {
          this.fx.pierceHit(e.x, e.y > 0.2 ? e.y : 1, e.z, e.d);
          this.stats.pierce++;
        }
        return false;
      case Ev.SkillCue: {
        if (e.b !== 0) return true;
        const name = cueNameOf(e.a | 0, e.c | 0, 'lyra', (e.flags & 2) !== 0 ? 1 : 0); // flags & 2 = 전직 갈래 1(트랩 레인저, C10-6)
        if (name === '') return true;
        const world = (e.flags & 1) !== 0;
        if (this.fx.playCue(name, e.x, e.y, e.z, e.d, lag, world ? 0 : -1)) this.stats.cues++;
        else this.stats.unknownCues++;
        return true;
      }
      case Ev.AwakenPhase:
        if (e.a === AwakenPhase.Gather) this.fx.playCue('lyra_awaken_charge', heroX, heroY, heroZ, facing, lag);
        return false;
      case Ev.AwakenSlash:
        this.fx.playCue('lyra_awaken_rain', e.x, 0, e.z, e.b, lag, 0);
        return false;
      default:
        return false;
    }
  }

  clear(): void {
    this.fx.clear();
    this.draw.value = 0;
  }
}
